import React from 'react';
import { motion } from 'framer-motion';
import { AudioWaveform as Waveform, Brain, Cpu, Zap } from 'lucide-react';
import { InfoTooltip } from './InfoTooltip';

interface ProcessingStatusProps {
  isProcessing: boolean;
  progress?: number;
}

export function ProcessingStatus({ isProcessing, progress = 0 }: ProcessingStatusProps) {
  if (!isProcessing) return null;

  const stages = [
    {
      name: 'Audio Analysis',
      description: 'Analyzing audio structure and characteristics',
      icon: Waveform,
      threshold: 25,
      color: 'blue'
    },
    {
      name: 'AI Processing',
      description: 'Applying neural network enhancements',
      icon: Brain,
      threshold: 50,
      color: 'purple'
    },
    {
      name: 'Neural Enhancement',
      description: 'Fine-tuning audio with AI models',
      icon: Zap,
      threshold: 75,
      color: 'cyan'
    },
    {
      name: 'Final Mastering',
      description: 'Applying professional mastering techniques',
      icon: Cpu,
      threshold: 100,
      color: 'emerald'
    }
  ] as const;

  const currentStage = stages.findIndex(stage => progress <= stage.threshold);
  const activeStage = currentStage === -1 ? stages.length - 1 : currentStage;

  return (
    <div className="mt-6 glass-panel p-6 cyber-border">
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="relative w-12 h-12">
            <motion.div
              className="absolute inset-0"
              animate={{ 
                rotate: 360,
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <div className="processing-ring" />
              <div className="processing-ring" style={{ animationDelay: '-0.5s' }} />
              <div className="processing-ring" style={{ animationDelay: '-1s' }} />
            </motion.div>
          </div>
          <div>
            <h4 className="font-medium text-blue-300">AI Mastering in Progress</h4>
            <p className="text-sm text-gray-400">Enhancing your audio with neural processing...</p>
          </div>
        </div>
        
        <div className="space-y-2">
          {stages.map((stage, index) => {
            const StageIcon = stage.icon;
            const isActive = index === activeStage;
            const isComplete = index < activeStage;
            const stageProgress = isActive 
              ? ((progress - (index > 0 ? stages[index - 1].threshold : 0)) / 
                 (stage.threshold - (index > 0 ? stages[index - 1].threshold : 0))) * 100
              : isComplete ? 100 : 0;
            
            return (
              <motion.div
                key={stage.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative group`}
              >
                <div className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300
                  ${isActive ? `bg-${stage.color}-900/20 text-${stage.color}-300` : 
                    isComplete ? 'bg-gray-800/50 text-gray-300' : 'bg-gray-800/20 text-gray-500'}`}
                >
                  <div className={`p-2 rounded-lg ${
                    isActive ? `bg-${stage.color}-500/20` : 
                      isComplete ? 'bg-gray-700' : 'bg-gray-800'
                  }`}>
                    <StageIcon className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{stage.name}</span>
                  {isComplete && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="ml-auto text-green-400"
                    >
                      ✓
                    </motion.div>
                  )}
                  {isActive && (
                    <motion.div
                      animate={{ 
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 1, 0.5]
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className={`ml-auto text-${stage.color}-400`}
                    >
                      Processing...
                    </motion.div>
                  )}
                </div>

                {/* Progress bar for active stage */}
                {isActive && (
                  <motion.div 
                    className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500"
                    initial={{ width: '0%' }}
                    animate={{ width: `${stageProgress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                )}

                {/* Stage info tooltip */}
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 opacity-0 
                              group-hover:opacity-100 transition-opacity duration-200">
                  <InfoTooltip
                    title={stage.name}
                    description={stage.description}
                    position="right"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Overall Progress</span>
            <span className="text-blue-300">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 rounded-full"
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between text-xs border-t border-gray-800 pt-4">
          <div className="flex items-center gap-2 text-gray-500">
            <Brain className="w-4 h-4" />
            <span>AI Mastering Engine v2.1</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Neural Processing</span>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}